import {
  preloadLegacyPath,
  registerForPreload,
} from "#src/utils/preloadRegistry.ts";
import React, { useEffect, useRef } from "react";
import { flushSync } from "react-dom";
import {
  Route as OriginalRoute,
  Router as OriginalRouter,
  Link as RouterLink,
  matchRoute as matchWouterRoute,
  useLocation as useOriginalLocation,
  useParams,
  useSearch,
} from "wouter";

interface RouteProps {
  exact?: boolean;
  match?: [boolean, Record<string, string | undefined> | null];
  render?: (props: RouteComponentProps<any>) => React.ReactNode;
  path?: string | string[];
  component?: React.ComponentType<any>;
  children?: React.ReactNode;
}

type RouteMatch = [boolean, Record<string, string | undefined> | null];
export type LegacyLocation = {
  pathname: string;
  search: string;
  hash: string;
  state: any;
};
export type LegacyHistoryAction = "PUSH" | "REPLACE" | "POP";
type LegacyNavigationOptions = {
  replace?: boolean;
  state?: any;
};
type HistoryListener = (update: {
  action: LegacyHistoryAction;
  location: LegacyLocation;
}) => void;
type HistoryBlocker =
  | false
  | ((nextLocation: LegacyLocation, action: LegacyHistoryAction) => boolean);
export type LegacyLocationDescriptor<TState = any> = {
  pathname: string;
  search?: string;
  hash?: string;
  state?: TState;
};
export type LegacyTo<TState = any> = string | LegacyLocationDescriptor<TState>;
type LegacyMatch<TParams = any> = {
  params: TParams;
};
export type LegacyRouterLocation<TState = any> = {
  pathname: string;
  search: string;
  state: TState;
  hash: string;
};
export type LegacyHistory = {
  push: (to: LegacyTo, state?: any) => void;
  replace: (to: LegacyTo, state?: any) => void;
  goBack: () => void;
  listen: (listener: HistoryListener) => () => void;
  block: (blocker: HistoryBlocker) => () => void;
  go: (delta: number) => void;
  length: number;
};
type LinkProps = Omit<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  "href" | "onMouseEnter" | "onMouseLeave"
> & {
  to?: LegacyTo;
  preloadDelay?: number;
  onMouseEnter?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  onMouseLeave?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
};

const historyListeners = new Set<HistoryListener>();
const historyBlockers = new Set<HistoryBlocker>();
let hasInstalledHistoryPopstateListener = false;
let historyEntries: string[] = [];
let currentHistoryEntryIndex = -1;
let isRestoringBlockedPopNavigation = false;

const getLegacyLocation = (): LegacyLocation => ({
  pathname: window.location.pathname,
  search: window.location.search,
  hash: window.location.hash,
  state: window.history.state,
});

const getLocationHref = (location: LegacyLocation) =>
  `${location.pathname}${location.search}${location.hash}`;

const ensureHistoryEntriesInitialized = () => {
  if (currentHistoryEntryIndex !== -1 || typeof window === "undefined") {
    return;
  }

  historyEntries = [getLocationHref(getLegacyLocation())];
  currentHistoryEntryIndex = 0;
};

const findHistoryEntryIndex = (href: string) => {
  if (currentHistoryEntryIndex < 0) {
    return -1;
  }

  const previousIndex = currentHistoryEntryIndex - 1;
  if (previousIndex >= 0 && historyEntries[previousIndex] === href) {
    return previousIndex;
  }

  const nextIndex = currentHistoryEntryIndex + 1;
  if (nextIndex < historyEntries.length && historyEntries[nextIndex] === href) {
    return nextIndex;
  }

  for (let offset = 2; offset < historyEntries.length; offset += 1) {
    const backwardIndex = currentHistoryEntryIndex - offset;
    if (backwardIndex >= 0 && historyEntries[backwardIndex] === href) {
      return backwardIndex;
    }

    const forwardIndex = currentHistoryEntryIndex + offset;
    if (
      forwardIndex < historyEntries.length &&
      historyEntries[forwardIndex] === href
    ) {
      return forwardIndex;
    }
  }

  return -1;
};

const syncHistoryEntries = (
  href: string,
  action: Exclude<LegacyHistoryAction, "POP">
) => {
  ensureHistoryEntriesInitialized();

  if (action === "REPLACE") {
    historyEntries[currentHistoryEntryIndex] = href;
    return;
  }

  historyEntries = historyEntries.slice(0, currentHistoryEntryIndex + 1);
  historyEntries.push(href);
  currentHistoryEntryIndex = historyEntries.length - 1;
};

const restoreBlockedPopNavigation = (nextIndex: number) => {
  const delta = nextIndex === -1 ? 1 : currentHistoryEntryIndex - nextIndex;

  if (delta === 0) {
    return;
  }

  isRestoringBlockedPopNavigation = true;
  window.history.go(delta);
};

const getLegacyLocationFromHref = (
  href: string,
  state?: any
): LegacyLocation => {
  const nextUrl = new URL(href, window.location.href);

  return {
    pathname: nextUrl.pathname,
    search: nextUrl.search,
    hash: nextUrl.hash,
    state,
  };
};

const notifyHistoryListeners = (action: LegacyHistoryAction) => {
  const location = getLegacyLocation();

  for (const listener of historyListeners) {
    listener({ action, location });
  }
};

const ensureHistoryPopstateListener = () => {
  if (hasInstalledHistoryPopstateListener || typeof window === "undefined") {
    return;
  }

  window.addEventListener("popstate", (event) => {
    ensureHistoryEntriesInitialized();

    if (isRestoringBlockedPopNavigation) {
      isRestoringBlockedPopNavigation = false;
      return;
    }

    const nextLocation = getLegacyLocation();
    const nextIndex = findHistoryEntryIndex(getLocationHref(nextLocation));

    if (isNavigationBlocked(nextLocation, "POP")) {
      event.stopImmediatePropagation();
      restoreBlockedPopNavigation(nextIndex);
      return;
    }

    if (nextIndex === -1) {
      historyEntries = historyEntries.slice(0, currentHistoryEntryIndex + 1);
      historyEntries.push(getLocationHref(nextLocation));
      currentHistoryEntryIndex = historyEntries.length - 1;
    } else {
      currentHistoryEntryIndex = nextIndex;
    }

    notifyHistoryListeners("POP");
  });

  hasInstalledHistoryPopstateListener = true;
};

const isNavigationBlocked = (
  nextLocation: LegacyLocation,
  action: LegacyHistoryAction
) => {
  for (const blocker of historyBlockers) {
    if (blocker === false) {
      return true;
    }

    if (
      typeof blocker === "function" &&
      blocker(nextLocation, action) === false
    ) {
      return true;
    }
  }

  return false;
};

const hasHardNavigationBlock = () => historyBlockers.has(false);

ensureHistoryPopstateListener();

const resolveLegacyTo = (to?: LegacyTo) => {
  if (!to) {
    return {
      href: "",
      state: undefined,
    };
  }

  if (typeof to === "string") {
    return {
      href: to,
      state: undefined,
    };
  }

  const search = to.search
    ? to.search.startsWith("?")
      ? to.search
      : `?${to.search}`
    : "";
  const hash = to.hash
    ? to.hash.startsWith("#")
      ? to.hash
      : `#${to.hash}`
    : "";

  return {
    href: `${to.pathname}${search}${hash}`,
    state: to.state,
  };
};

const resolveHistoryNavigation = (to: LegacyTo, state?: any) => {
  const resolved = resolveLegacyTo(to);

  return {
    href: resolved.href,
    state: state === undefined ? resolved.state : state,
  };
};

const attemptLegacyNavigation = (
  navigate: (to: string, options?: LegacyNavigationOptions) => void,
  to: string,
  options?: LegacyNavigationOptions
) => {
  const action: LegacyHistoryAction = options?.replace ? "REPLACE" : "PUSH";
  const nextLocation = getLegacyLocationFromHref(to, options?.state);
  const nextHref = getLocationHref(nextLocation);

  if (isNavigationBlocked(nextLocation, action)) {
    return false;
  }

  if (
    !document.startViewTransition ||
    document.visibilityState === "hidden"
  ) {
    navigate(to, options);
  } else {
    const transition = document.startViewTransition(() => {
      flushSync(() => {
        navigate(to, options);
      });
    });

    transition.ready.catch((err) => {
      if (
        err.name === "AbortError" ||
        (err.name === "InvalidStateError" &&
          err.message ===
            "View transition was skipped because document visibility state is hidden.")
      ) {
        console.log("View transition was skipped gracefully.");
      } else {
        throw err; // Re-throw actual unexpected bugs
      }
    });
  }

  syncHistoryEntries(nextHref, action);
  notifyHistoryListeners(action);
  return true;
};

export interface RouteComponentProps<
  TParams = any,
  TStaticContext = any,
  TState = any,
> {
  match: LegacyMatch<TParams>;
  history: LegacyHistory;
  location: LegacyRouterLocation<TState>;
  staticContext?: TStaticContext;
}

export type match<T = any> = LegacyMatch<T>;

export function matchPath(
  pathname: string,
  options: { path: string; exact?: boolean }
) {
  const [matched] = matchWouterRoute(
    strictParser,
    options.path,
    pathname,
    !options.exact
  );

  return matched;
}

const parserCache = new Map<string, { pattern: RegExp; keys: string[] }>();

const escapeRegex = (value: string) =>
  value.replace(/[|\\{}()[\]^$+*?.-]/g, "\\$&");

const parseLegacyParamSegment = (segment: string) => {
  if (!segment.startsWith(":")) {
    return null;
  }

  let index = 1;
  while (/[$\w]/.test(segment[index] || "")) {
    index += 1;
  }

  const name = segment.slice(1, index);
  if (!name) {
    return null;
  }

  let pattern = "[^/]+";

  if (segment[index] === "(") {
    let depth = 1;
    let cursor = index + 1;

    while (cursor < segment.length && depth > 0) {
      const char = segment[cursor];

      if (char === "\\") {
        cursor += 2;
        continue;
      }

      if (char === "(") {
        depth += 1;
      } else if (char === ")") {
        depth -= 1;
      }

      cursor += 1;
    }

    if (depth !== 0) {
      throw new TypeError(`Invalid legacy route pattern: ${segment}`);
    }

    pattern = segment.slice(index + 1, cursor - 1);
    index = cursor;
  }

  const optional = segment[index] === "?";

  if (segment.slice(index + (optional ? 1 : 0)).length > 0) {
    return null;
  }

  return {
    name,
    optional,
    pattern: `(${pattern})`,
  };
};

const compileLegacyRoute = (path: string, loose?: boolean) => {
  const cacheKey = `${loose ? "loose" : "strict"}:${path}`;
  const cached = parserCache.get(cacheKey);

  if (cached) {
    return cached;
  }

  if (path === "*") {
    const wildcard = {
      pattern: /^.*$/,
      keys: ["path"],
    };

    parserCache.set(cacheKey, wildcard);
    return wildcard;
  }

  const keys: string[] = [];
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;

  if (normalizedPath === "/") {
    const root = {
      pattern: loose ? /^\/.*/ : /^\/?$/,
      keys,
    };

    parserCache.set(cacheKey, root);
    return root;
  }

  const segments = normalizedPath.split("/").slice(1);
  let source = "^";

  for (const segment of segments) {
    if (!segment) {
      source += "/";
      continue;
    }

    if (segment.startsWith("*")) {
      const name = segment.slice(1) || "path";
      keys.push(name);
      source += "/(.*)";
      continue;
    }

    const param = parseLegacyParamSegment(segment);

    if (param) {
      keys.push(param.name);
      source += param.optional ? `(?:/${param.pattern})?` : `/${param.pattern}`;
      continue;
    }

    source += `/${escapeRegex(segment)}`;
  }

  source += loose ? "(?:/|$)" : "/?$";

  const compiled = {
    pattern: new RegExp(source),
    keys,
  };

  parserCache.set(cacheKey, compiled);
  return compiled;
};

const strictParser = (path: string, loose?: boolean) => {
  const pattern = compileLegacyRoute(path, loose);

  return {
    pattern: pattern.pattern,
    keys: pattern.keys,
  };
};

export const BrowserRouter: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return (
    <OriginalRouter
      parser={strictParser}
      aroundNav={(navigate, to, options) => {
        attemptLegacyNavigation(
          navigate as (to: string, options?: LegacyNavigationOptions) => void,
          to,
          options as LegacyNavigationOptions | undefined
        );
      }}
    >
      {children}
    </OriginalRouter>
  );
};

const toLegacyRouteProps = (
  params: ReturnType<typeof useParams>,
  history: LegacyHistory,
  location: LegacyRouterLocation<any>
): RouteComponentProps<any> => ({
  match: {
    params,
  },
  history,
  location,
});

const resolveMatch = (
  path: RouteProps["path"],
  pathname: string,
  exact?: boolean,
  routeMatch?: RouteProps["match"]
): RouteMatch => {
  if (routeMatch) return routeMatch;
  if (!path) return [true, {}];

  const paths = Array.isArray(path) ? path : [path];

  for (const currentPath of paths) {
    const [matched, params] = matchWouterRoute(
      strictParser,
      currentPath,
      pathname,
      !exact
    );

    if (matched) {
      return [true, params];
    }
  }

  return [false, null];
};

const flattenChildren = (children: React.ReactNode): React.ReactNode[] => {
  const items: React.ReactNode[] = [];

  React.Children.forEach(children, (child) => {
    if ((child as any)?.type === React.Fragment) {
      items.push(...flattenChildren((child as any).props?.children));
      return;
    }

    items.push(child);
  });

  return items;
};

const registerComponentPreload = (
  path: RouteProps["path"],
  exact: boolean,
  component: RouteProps["component"]
) => {
  if (!component) return;

  const paths = Array.isArray(path) ? path : [path || "*"];
  for (const currentPath of paths) {
    registerForPreload(currentPath as string, exact, component);
  }
};

export const Route: React.FC<RouteProps> = ({
  path,
  exact,
  component,
  render,
  match,
  children,
  ...props
}) => {
  const location = useLocation();
  const history = useHistory();

  useEffect(() => {
    registerComponentPreload(path, !!exact, component);
  }, [component, path, exact]);

  const resolvedMatch = resolveMatch(path, location.pathname, exact, match);
  const RouteComponent: any = OriginalRoute;

  return (
    <RouteComponent path={path} match={resolvedMatch} {...props}>
      {(params) => {
        const routeProps = toLegacyRouteProps(params, history, location);

        if (component) {
          const Component: any = component;

          return <Component {...routeProps} />;
        }

        if (render) {
          return render(routeProps);
        }

        return typeof children === "function"
          ? (children as (params: typeof routeProps.match) => React.ReactNode)(
              routeProps.match
            )
          : children;
      }}
    </RouteComponent>
  );
};

export const Switch: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Switch reads all children immediately to find a match.
  // We can hook into this to register all sibling routes before they render!
  React.Children.forEach(children, (child) => {
    if (React.isValidElement<RouteProps>(child) && child.props.component) {
      registerComponentPreload(
        child.props.path,
        !!child.props.exact,
        child.props.component
      );
    }
  });

  const pathname = useLocation().pathname;

  for (const child of flattenChildren(children)) {
    if (!React.isValidElement(child)) {
      continue;
    }

    const routeChild = child as React.ReactElement<RouteProps>;

    const routeMatch = resolveMatch(
      routeChild.props.path,
      pathname,
      routeChild.props.exact,
      routeChild.props.match
    );

    if (routeMatch[0]) {
      return React.cloneElement(routeChild, { match: routeMatch });
    }
  }

  return null;
};

export const Link = ({
  preloadDelay = 100,
  onMouseEnter,
  onMouseLeave,
  to = "",
  ...props
}: LinkProps) => {
  const { href: targetPath } = resolveLegacyTo(to);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (targetPath) {
      if (preloadDelay > 0) {
        timeoutRef.current = setTimeout(() => {
          preloadLegacyPath(targetPath);
        }, preloadDelay);
      } else {
        preloadLegacyPath(targetPath);
      }
    }
    if (onMouseEnter) onMouseEnter(e);
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (onMouseLeave) onMouseLeave(e);
  };

  return (
    <RouterLink
      to={targetPath}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...props}
    />
  );
};

export function useLocation<T = any>(): LegacyRouterLocation<T> {
  const [location, _] = useOriginalLocation();
  const searchString = useSearch();

  return {
    pathname: location,
    search: searchString ? `?${searchString}` : "",
    state: window.history.state as T,
    hash: window.location.hash,
  };
}

export function useHistory(): LegacyHistory {
  const [_, navigate] = useOriginalLocation();

  React.useEffect(() => {
    ensureHistoryPopstateListener();
  }, []);

  const navigateWithHistory = (
    to: LegacyTo,
    action: Exclude<LegacyHistoryAction, "POP">,
    state?: any
  ) => {
    const { href, state: nextState } = resolveHistoryNavigation(to, state);

    navigate(href, { replace: action === "REPLACE", state: nextState });
  };

  return {
    push: (to: LegacyTo, state?: any) => navigateWithHistory(to, "PUSH", state),
    replace: (to: LegacyTo, state?: any) =>
      navigateWithHistory(to, "REPLACE", state),
    goBack: () => {
      if (hasHardNavigationBlock()) {
        return;
      }

      window.history.back();
    },
    listen: (listener: HistoryListener) => {
      historyListeners.add(listener);

      return () => {
        historyListeners.delete(listener);
      };
    },
    block: (blocker: HistoryBlocker) => {
      historyBlockers.add(blocker);

      return () => {
        historyBlockers.delete(blocker);
      };
    },
    go: (delta: number) => {
      if (delta === 0) {
        return;
      }

      if (hasHardNavigationBlock()) {
        return;
      }

      window.history.go(delta);
    },
    length: window.history.length,
  };
}

export function withRouter(Component: React.ComponentType<any>) {
  return function RouterComponent(props: any) {
    const history = useHistory();
    const location = useLocation();
    const params = useParams();

    return (
      <Component
        {...props}
        match={{
          params,
        }}
        history={history}
        location={location}
      />
    );
  };
}

export function Redirect({
  to,
  push = false,
}: {
  to: LegacyTo;
  push?: boolean;
}): React.ReactElement | null {
  const history = useHistory();
  const { href, state } = resolveLegacyTo(to);

  useEffect(() => {
    if (push) {
      history.push(href, state);
      return;
    }

    history.replace(href, state);
  }, [history, href, push, state]);

  return null;
}

export { useParams };
